import type { CSSProperties, Ref } from 'vue'

import { computed, inject } from 'vue'

interface UseSelectorStickyOptions {
  withSelector: Ref<boolean>
  backgroundColor?: string
  stickyColumns?: Ref<number>
  /** Optional: pass directly instead of injecting (for parent component) */
  scrollLeft?: Ref<number>
}

interface UseSelectorStickyReturn {
  isSelectorSticky: Ref<boolean>
  selectorStickyStyle: Ref<CSSProperties>
}

/**
 * Composable for sticky selector column behavior in sf-virtual-table.
 * The selector column (checkbox) is always the leftmost and has the highest z-index.
 *
 * @param options.withSelector - Reactive ref indicating if selector is enabled
 * @param options.backgroundColor - Optional background color (default: 'white')
 */
export function useSelectorSticky(
  options: UseSelectorStickyOptions
): UseSelectorStickyReturn {
  const { withSelector, backgroundColor = 'white' } = options

  const stickyColumns =
    options.stickyColumns ?? inject<Ref<number>>('stickyColumns')
  const scrollLeft = options.scrollLeft ?? inject<Ref<number>>('scrollLeft')

  const isSelectorSticky = computed<boolean>(
    () => withSelector.value && !!stickyColumns && stickyColumns.value > 0
  )

  const selectorStickyStyle = computed<CSSProperties>(() => {
    if (!isSelectorSticky.value || !scrollLeft) return {}
    return {
      transform: `translate3d(${scrollLeft.value}px, 0, 0)`,
      zIndex: 11,
      position: 'relative',
      backgroundColor,
      willChange: 'transform',
      backfaceVisibility: 'hidden',
    }
  })

  return {
    isSelectorSticky,
    selectorStickyStyle,
  }
}
